import { Badge, Button, Colors, useProgress, ProgressDisplay } from "@factorearth/component-library";
import { uploadData } from "aws-amplify/storage";
import { FormGroup, FormInstance, FormTemplateForResolver, Project, ProjectForResolver, Source, SyncConfig, UserForResolver } from "db/definitions";
import { downloadFormGroupJson, mergeAndSave, prepDbForMerge } from "db/merge";
import React, { useEffect, useRef, useState } from "react";
import { FiCheck } from "react-icons/fi";
import { recordSentry } from "utils/recordSentry";
import { syncTemplateWarningMessage } from "utils/syncTemplateWarningMessage";
import { checkIfTemplatesSaved } from "../../../db/queries/formTemplateQueries";
import { getProjectAndFormGroupsForSyncTab } from "../../../db/queries/projectQueries";
import FormGroupList from "../../FormGroupList/FormGroupList";
import ProjectList from "../../ProjectList/ProjectList";
import OfflineError from "./OfflineError/OfflineError";
import styles from "./SyncProject.module.scss";
import TemplateSyncMenu from "./TemplateSyncMenu/TemplateSyncMenu";
import { Location } from "hooks/useDrawer";
import { RecentProject } from "utils/localstorage/projects";
import { useDownloadImagesModal } from "hooks/useDownloadImageModal";

interface Props {
	addFormGroupsInState: (formGroups: FormGroup[]) => void;
	color: Colors;
	estimateReadable: string | null;
	formGroups: FormGroup[];
	formTemplates: FormTemplateForResolver[];
	online: boolean;
	persisted: boolean;
	project: Project | null;
	projectId: string;
	projects: ProjectForResolver[];
	quota: number;
	quotaReadable: string;
	recentProjects: RecentProject[];
	redirectToSyncMenu: boolean;
	selected: string[];
	setProjectId: React.Dispatch<React.SetStateAction<string>>;
	setSelected: React.Dispatch<React.SetStateAction<string[]>>;
	setupDrawer: (location: Location | "") => void;
	updateProjectsSyncMenu: (project: Project) => void;
	usage: number;
	usageReadable: string;
	user: UserForResolver | undefined | null;
	userErrorMessage: string;
}

function SyncProject(props: Props) {
	const {
		addFormGroupsInState,
		color,
		estimateReadable,
		formGroups,
		formTemplates,
		online,
		persisted,
		project,
		projectId,
		projects,
		quota,
		quotaReadable,
		recentProjects,
		redirectToSyncMenu,
		selected,
		setProjectId,
		setSelected,
		setupDrawer,
		usage,
		usageReadable,
		user,
		userErrorMessage,
		updateProjectsSyncMenu,
	} = props;

	// These are auto populated by the useEffect
	const [offlineFormGroups, setOfflineFormGroups] = useState<FormGroup[]>([]);
	const [offlineProject, setOfflineProject] = useState<Project | null>(null);
	const [loading, setLoading] = useState(false);
	const [mergeSuccess, setMergeSuccess] = useState<"" | "loading" | "success">("");
	const syncInProgressRef = useRef(false);
	const [templatesSaved, setTemplatesSaved] = useState(false);
	const [showProjectSyncMenu, setShowProjectSyncMenu] = useState(!redirectToSyncMenu);
	const { progress, setProgress } = useProgress({
		initial: {
			value: 0,
			variant: "info",
			message: "",
			title: "",
			canClose: false,
			show: false,
		},
	});
	useEffect(() => {
		async function checkTemplates() {
			const templatesSavedCheck = await checkIfTemplatesSaved();
			setTemplatesSaved(templatesSavedCheck);
		}
		checkTemplates();
	}, []);

	useEffect(() => {
		if (mergeSuccess !== "loading") return;
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = "Sync is in progress. Leaving now may cause data loss.";
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [mergeSuccess]);

	// Whenever the project changes, grabs all offline projects that have the same id as the online project
	useEffect(() => {
		async function getOfflineFormGroups() {
			setLoading(true);
			const result = await getProjectAndFormGroupsForSyncTab(projectId);
			if (result) {
				const completeProject = result;
				setOfflineFormGroups(completeProject?.formGroups?.items || []);
				setOfflineProject(completeProject);
			} else {
				setOfflineFormGroups([]);
				setOfflineProject(null);
			}
			setLoading(false);
		}
		if (projectId) {
			getOfflineFormGroups();
		}
	}, [projectId]);

	const [DownloadImagesModal, showDownloadImagesModal] = useDownloadImagesModal();

	/**
	 * Takes the selected formGroups and saves them offline
	 * @author Eric Webb <ewebb@factorearth.com>
	 */
	async function getFormGroups() {
		if (selected.length > 0 && (project || offlineProject)) {
			const syncConfig: SyncConfig = {
				online,
				source: "remote",
				destination: "local",
			};
			setProgress({
				value: 0,
				variant: "info",
				message: "Preparing to merge...",
				title: "Syncing FormGroups for Offline Editing",
				canClose: false,
				show: true,
			});
			const result = await mergeLocalOrRemoteToOpposite(syncConfig);
			if (result) {
				setProgress({
					value: 80,
					variant: "info",
					message: "Updating State...",
					title: "Syncing FormGroups for Offline Editing",
					canClose: false,
					show: true,
				});
				const { mergeDataToSave, donorData } = result;
				const offlineCopy = [...offlineFormGroups];
				mergeDataToSave.formGroups.forEach((formGroup) => {
					const fgCopy: FormGroup = { ...formGroup };
					const formInstances = [];
					for (const formId of fgCopy.formOrder) {
						// Get instance
						const formInstance = donorData.formInstances.get(formId);
						if (formInstance) {
							const formCopy: FormInstance = { ...formInstance, fieldInstances: { items: [] } };
							const formTemplate = donorData.formTemplates.get(formCopy.formTemplateId);
							if (formTemplate) {
								const formTemplateCopy = { ...formTemplate };
								formCopy.formTemplate = formTemplateCopy;
							}
							formInstances.push(formCopy);
						}
					}
					fgCopy.formInstances = { items: formInstances };
					const foundIndex = offlineCopy.findIndex((fg) => fg.id === fgCopy.id);
					if (foundIndex !== -1) {
						offlineCopy[foundIndex] = fgCopy;
					} else {
						offlineCopy.push(fgCopy);
					}
				});
				setOfflineFormGroups(offlineCopy);
				setProgress({
					value: 100,
					variant: mergeDataToSave.templateWarnings?.length ? "info" : "success",
					message: syncTemplateWarningMessage("FormGroups Successfully Synced Offline", mergeDataToSave.templateWarnings),
					title: "FormGroups Successfully Synced Offline",
					timeout: mergeDataToSave.templateWarnings?.length ? undefined : 5000,
					canClose: true,
					show: true,
				});
				const templatesSavedCheck = await checkIfTemplatesSaved();
				setTemplatesSaved(templatesSavedCheck);
			} else {
				setProgress({
					value: 80,
					variant: "destructive",
					message: "Failed to sync FormGroups for Offline Editing",
					title: "Failed to sync FormGroups for Offline Editing",
					canClose: true,
					show: true,
				});
			}
		}
	}

	/**
	 * Handles the logic of merging offline data to online
	 * @author Eric Webb <ewebb@factorearth.com>
	 */
	async function mergeButton() {
		if (syncInProgressRef.current) return;
		syncInProgressRef.current = true;
		setMergeSuccess("loading");
		try {
			if (selected.length > 0 && (project || offlineProject)) {
				const syncConfig: SyncConfig = {
					online,
					source: "local",
					destination: "remote",
				};
				setProgress({
					value: 0,
					variant: "info",
					message: "Preparing to merge...",
					title: "Syncing Offline FormGroups to Online",
					canClose: false,
					show: true,
				});
				// The local snapshot must be on S3 before the merge writes anything; the function never rejects.
				if (offlineProject) await downloadSelectedFormGroupsAndBackup(offlineProject.formGroups.items);
				const result = await mergeLocalOrRemoteToOpposite(syncConfig);
				if (result) {
					const { mergeDataToSave, donorData } = result;
					setProgress({
						value: 80,
						variant: "info",
						message: "Updating State...",
						title: "Syncing Offline FormGroups to Online",
						canClose: false,
						show: true,
					});
					const onlineCopy = [...formGroups];
					mergeDataToSave.formGroups.forEach((formGroup) => {
						const fgCopy: FormGroup = { ...formGroup };
						const formInstances = [];
						for (const formId of fgCopy.formOrder) {
							// Get instance
							const formInstance = donorData.formInstances.get(formId);
							if (formInstance) {
								const formCopy: FormInstance = { ...formInstance, fieldInstances: { items: [] } };
								const formTemplate = donorData.formTemplates.get(formCopy.formTemplateId);
								if (formTemplate) {
									const formTemplateCopy = { ...formTemplate };
									formCopy.formTemplate = formTemplateCopy;
								}
								formInstances.push(formCopy);
							}
						}
						fgCopy.formInstances = { items: formInstances };
						const foundIndex = onlineCopy.findIndex((fg) => fg.id === fgCopy.id);
						if (foundIndex !== -1) {
							onlineCopy[foundIndex] = fgCopy;
						} else {
							onlineCopy.push(fgCopy);
						}
					});

					addFormGroupsInState(onlineCopy);
					const projectCopy = { ...mergeDataToSave.projects[0], formGroups: { items: onlineCopy } };
					updateProjectsSyncMenu(projectCopy);

					setMergeSuccess("success");
					setProgress({
						value: 100,
						variant: mergeDataToSave.templateWarnings?.length ? "info" : "success",
						message: syncTemplateWarningMessage("FormGroups Successfully Synced Online", mergeDataToSave.templateWarnings),
						title: "FormGroups Successfully Synced Online",
						timeout: mergeDataToSave.templateWarnings?.length ? undefined : 5000,
						canClose: true,
						show: true,
					});
					setTimeout(() => {
						setMergeSuccess("");
					}, 10000);
				} else {
					setProgress({
						value: 100,
						variant: "destructive",
						message: "Failed to sync Offline FormGroups to Online. Nothing was changed.",
						title: "Failed to sync Offline FormGroups to Online",
						canClose: true,
						show: true,
					});
				}
			}
		} finally {
			syncInProgressRef.current = false;
			setMergeSuccess("");
		}
	}

	/**
	 * Downloads all selected formGroups and its child instances (NOT TEMPLATES) into a json file, named with their username and timestamp
	 */
	async function downloadSelectedFormGroupsAndBackup(selected: FormGroup[]) {
		// Close the options menu
		try {
			// Get the formGroups
			const syncConfig: SyncConfig = {
				online,
				source: "local",
				destination: "json",
			};

			const projectToUse = offlineProject || project;
			if (projectToUse && user) {
				const selectedFormGroupIds = selected.map((fg) => fg.id);
				const jsonStructure = await downloadFormGroupJson(projectToUse, selectedFormGroupIds, syncConfig);

				// const stringifiedJson = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(jsonStructure))}`;
				const stringifiedJson = JSON.stringify(jsonStructure);

				// Create download anchor + download
				const date = new Date();
				const year = date.getFullYear().toString().slice(-2); // Last 2 digits of the year
				const month = (date.getMonth() + 1).toString().padStart(2, "0"); // The month with a leading zero if less than 2 characters
				const day = date.getDate().toString().padStart(2, "0"); // The day with a leading 0 if it's less than 2 characters
				const hour = date.getHours().toString().padStart(2, "0"); // The hour with a leading 0 if less than 2 characters
				const minute = date.getMinutes().toString().padStart(2, "0"); // The minutes with a leading 0 if less than 2 characters
				const seconds = date.getSeconds().toString().padStart(2, "0"); // The seconds with a leading 0 if less than 2 characters
				const finalTime = `${hour}${minute}${seconds} ${month}-${day}-${year}`;
				// downloadAnchorNode.setAttribute("download", `${finalTime}|${project.name}|${user?.username || "UnknownUser"}.json`);
				const url = `${user.group}/sync-backups/${projectToUse.name}/${user?.username || "UnknownUser"}-${finalTime}.json`;
				try {
					await uploadData({
						key: url,
						// Alternatively, path: ({identityId}) => `protected/${identityId}/album/2024/1.jpg`
						data: stringifiedJson,
					}).result;
				} catch (error) {
					const message = "ERROR SAVING JSON TO S3 BUCKET";
					recordSentry({ error, msg: message });
				}
			}
		} catch (err) {
			const message = "ERROR DOWNLOADING JSON TO SAVE TO S3";
			recordSentry({ error: err, msg: message });
		}
	}

	/**
	 * Given a sync config, handles fetching the relevant stuff and merging them to the correct db
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param syncConfig The sync config
	 * @returns Everything from the merge process so we can rebuild formGroups as necessary
	 */
	async function mergeLocalOrRemoteToOpposite(syncConfig: SyncConfig) {
		if (selected.length > 0 && (project || offlineProject) && user) {
			const oppositeMap: Record<Source, Source> = {
				local: "remote",
				remote: "local",
				json: "json", // Makes TS happy, not actually configured yet
			};
			const forceUpdateData = `REFRESH_${Date.now()}`;
			const sourceProjCopy: Project | undefined =
				syncConfig.source === "local"
					? offlineProject
						? { ...offlineProject, forceUpdateData }
						: undefined
					: project
						? { ...project, forceUpdateData }
						: undefined;
			const destinationProjCopy: Project | undefined =
				syncConfig.destination === "remote"
					? project
						? { ...project, forceUpdateData }
						: undefined
					: offlineProject
						? { ...offlineProject, forceUpdateData }
						: undefined;
			if (sourceProjCopy) {
				try {
					setProgress({
						value: 20,
						variant: "info",
						message: `Preparing ${syncConfig.source} database for merge`,
						title: "Syncing FormGroups",
						canClose: false,
						show: true,
					});
					const donorData = await prepDbForMerge(user, syncConfig, selected, sourceProjCopy);
					if (donorData.imagesCouldBeDownloaded) {
						syncConfig.savePhotos = !!(await showDownloadImagesModal());
					}
					setProgress({
						value: 40,
						variant: "info",
						message: `Preparing ${syncConfig.destination} database for merge`,
						title: "Syncing FormGroups",
						canClose: false,
						show: true,
					});
					const destinationSyncConfig: SyncConfig = {
						online,
						source: oppositeMap[syncConfig.source],
						destination: oppositeMap[syncConfig.destination],
					};
					const receiverData = await prepDbForMerge(user, destinationSyncConfig, selected, destinationProjCopy);
					setProgress({
						value: 60,
						variant: "info",
						message: `Merging ${syncConfig.source} data to ${syncConfig.destination} database`,
						title: "Syncing FormGroups",
						canClose: false,
						show: true,
					});
					const mergeDataToSave = await mergeAndSave(donorData, receiverData, syncConfig, { username: user?.username });
					return {
						mergeDataToSave,
						donorData,
						receiverData,
					};
				} catch (error) {
					// mergeAndSave refuses to merge when the templates for a form cannot be found on either side. Returning
					// undefined lets the callers show their "Failed to sync" state instead of leaving the progress modal stuck.
					recordSentry({ error, msg: `Sync ${syncConfig.source} -> ${syncConfig.destination} failed` });
					return undefined;
				}
			}
		}
	}

	function handleChangeProject(e: { target: { value: string } }) {
		const { value } = e.target;
		setProjectId(value);
	}

	function toggleViews() {
		setShowProjectSyncMenu((prevView) => !prevView);
	}
	return userErrorMessage ? (
		<OfflineError color={color} errorMessage={userErrorMessage} />
	) : (
		<div className={styles.syncWrapper}>
			{progress.show && <ProgressDisplay colors={color} progress={progress} setProgress={setProgress} />}
			<DownloadImagesModal color={color} />
			<div className={styles.toolbar_div}>
				<div className={styles.exit_btn}>
					<Button colorPalette={color} title="Sync Template Menu" className={styles.project_button} onClick={toggleViews} variant="filled">
						{`Go to ${showProjectSyncMenu ? "Template" : "Project"} Sync Menu`}
					</Button>
				</div>
				{showProjectSyncMenu && (
					<div className={styles.syncProjectList}>
						<ProjectList
							color={color}
							handleChangeProject={handleChangeProject}
							online={online}
							projectId={projectId}
							projects={projects as Project[]}
							recentProjects={recentProjects}
						/>
					</div>
				)}
			</div>
			<div className={styles.project_wrapper}>
				{user && (
					<div className={styles.toolbarVisible}>
						{showProjectSyncMenu ? (
							<>
								<div className={styles.storageDiv}>
									<div style={{ color: color.text.primary }} className={styles.storageHeader}>
										<p className={styles.storageHeaderText}>
											Offline Storage. You {persisted ? "are using" : "aren't using"} persisted storage
										</p>
										<p className={styles.storageHeaderNumber}>
											&nbsp;{estimateReadable ? estimateReadable : `${usageReadable}/${quotaReadable}`}
										</p>
									</div>
									{!estimateReadable ? (
										<div className={styles.storageWrapper}>
											<div
												style={{ background: color.background.tertiary, border: `solid 1px ${color.background.secondary}` }}
												className={styles.storageUsedBar}>
												<div
													style={{
														background: color.text.secondary,
														width: (usage / quota) * 100 < 3 && (usage / quota) * 100 > 0 ? "3%" : `${(usage / quota) * 100}%`,
													}}
													className={styles.storageUsed}></div>
											</div>
										</div>
									) : null}
								</div>
								<div className={styles.syncMenu_wrapper}>
									<div className={styles.syncMenu_window}>
										<div className={styles.syncHeaderContainer}>
											<h3 style={{ color: color.text.primary }} className={styles.syncHeader}>
												Offline Manager
											</h3>
											<p style={{ color: color.text.primary }} className={styles.syncInfo}>
												{online
													? "Save selected form groups to the cloud to merge them into your online project."
													: "Hop Online to Merge your Offline Projects."}
											</p>
											<Button colorPalette={color} className={styles.project_button} onClick={mergeButton} variant="filled">
												Save Selected to Cloud
											</Button>
										</div>
										<div
											style={{
												color: color.text.primary,
												borderBottom: `${color.background.primary} 1px solid`,
											}}
											className={styles.sync_header}>
											<h4 className={styles.sync_header_text}>Offline Forms</h4>
											{templatesSaved ? (
												<Badge label="Templates Saved" theme={color} variant={"success"} icon={<FiCheck />} />
											) : (
												<Badge label="Templates Not Saved" theme={color} variant={"destructive"} />
											)}
										</div>
										{project || offlineProject ? (
											offlineFormGroups.length > 0 ? (
												<div className={styles.formGroup_sync}>
													<FormGroupList
														color={color}
														formGroups={offlineFormGroups}
														online={false}
														selected={selected}
														setSelected={setSelected}
														setupDrawer={setupDrawer}
														user={user}
													/>
												</div>
											) : loading ? (
												<div className={styles.loading}>Loading...</div>
											) : (
												<div style={{ color: color.text.primary }} className={styles.no_data}>
													No Form Groups saved offline in {project?.name || offlineProject?.name}.
												</div>
											)
										) : (
											<>
												<div style={{ color: color.text.primary }} className={styles.no_data}>
													To access the offline forms for a particular project or save forms for offline use, first choose a project.
												</div>
												<div style={{ color: color.text.primary }} className={styles.no_data}>
													Once you’re ready to save a project for offline use, make sure you have saved all the necessary templates if
													they are not already saved.
												</div>
											</>
										)}
									</div>
									<div
										style={
											{
												// background: color.background.secondary,
											}
										}
										className={styles.syncMenu_window}>
										<div className={styles.syncHeaderContainer}>
											<h3 style={{ color: color.text.primary }} className={styles.syncHeader}>
												Online Manager
											</h3>
											<p style={{ color: color.text.primary }} className={styles.syncInfo}>
												Save form groups from your project to work in offline mode.
											</p>
											<Button colorPalette={color} className={styles.project_button} onClick={getFormGroups} variant="filled">
												Save Selected to Device
											</Button>
										</div>
										<div
											style={{
												color: color.text.primary,
												borderTop: `${color.background.primary} 1px solid`,
												borderBottom: `${color.background.primary} 1px solid`,
											}}
											className={styles.sync_header}>
											<h4 className={styles.sync_header_text}>Online Forms</h4>
											{mergeSuccess === "loading" && <div className={styles.mergeSuccess}>Merging...</div>}
											{mergeSuccess === "success" && <div className={styles.mergeSuccess}>Merge Successful ✓</div>}
										</div>
										{project ? (
											formGroups.length > 0 ? (
												<div className={styles.formGroup_sync}>
													<FormGroupList
														color={color}
														formGroups={formGroups}
														selected={selected}
														setSelected={setSelected}
														setupDrawer={setupDrawer}
														user={user}
														online={false}
													/>
												</div>
											) : (
												<div style={{ color: color.text.primary }} className={styles.no_data}>
													You are currently working in the {project.name} Project.
												</div>
											)
										) : (
											<div style={{ color: color.text.primary }} className={styles.no_data}>
												<p className={styles.no_data_no_project}>No Project Selected.</p>
												<p>You can Select a Project from the dropdown above.</p>
											</div>
										)}
									</div>
								</div>
							</>
						) : (
							<div className={styles.tsm_wrapper}>
								<TemplateSyncMenu color={color} formTemplates={formTemplates} userDoc={user} />
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}

export default SyncProject;
